import React from 'react';
import { Task } from '../../types/task';
import { useLanguage } from '../../contexts/LanguageContext';
import Modal from '../common/Modal';
import TaskComments from './TaskComments';
import { ChatBubbleLeftIcon, ClipboardDocumentListIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface TaskCommentsModalProps {
  task: Task;
  isOpen: boolean;
  onClose: () => void;
  onAddComment: (message: string, files?: File[]) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  onUpdateComment?: (commentId: string, message: string) => Promise<void>;
  onDeleteAttachment?: (commentId: string, attachmentId: string) => Promise<void>;
  onExportTask?: () => void;
}

const TaskCommentsModal: React.FC<TaskCommentsModalProps> = ({
  task,
  isOpen,
  onClose,
  onAddComment,
  onDeleteComment,
  onUpdateComment,
  onDeleteAttachment,
  onExportTask
}) => {
  const { t } = useLanguage();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="5xl"
      zIndex={60}
    >
      {/* Custom Header - időmérő stílusban */}
      <div className="-m-6 mb-6 px-6 py-5 bg-white dark:bg-gray-800 border-b-2 border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg">
              <ChatBubbleLeftIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('tasks.sections.comments') || 'Megjegyzések'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {task.taskNumber} - {task.title}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {onExportTask && (
              <button
                onClick={onExportTask}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span>{t('tasks.actions.export') || 'Export'}</span>
              </button>
            )}
            <div className="text-right">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {t('tasks.sections.comments') || 'Megjegyzések'}
              </div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {task.comments?.length || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kommentek szekció */}
      <div className="space-y-6">
        <TaskComments
          task={task}
          onAddComment={onAddComment}
          onDeleteComment={onDeleteComment}
          onUpdateComment={onUpdateComment}
          onDeleteAttachment={onDeleteAttachment}
        />
      </div>
    </Modal>
  );
};

export default TaskCommentsModal;
